import { LcmStudioChannels } from "@hightouch/lib/llm/lifecycle-studio/types";

/**
 * Origin a candidate message editor was opened from, stamped on the URL as
 * `?from=`. The candidates-nested editor route
 * (`/projects/:campaignId/candidates/messages/...`) is shared by several
 * surfaces, so the path alone can't tell them apart — the editor's back arrow
 * resolves its target from this param. The candidates tab stamps nothing:
 * it's the fallback when the param is absent (direct links, refreshes).
 */
export const MessageEditorOrigins = {
  Orchestration: "orchestration",
  Plan: "plan",
  Messages: "messages",
} as const;

export type MessageEditorOrigin =
  (typeof MessageEditorOrigins)[keyof typeof MessageEditorOrigins];

const MESSAGE_EDITOR_ORIGIN_VALUES = Object.values(MessageEditorOrigins);

/** Narrow an untrusted `?from=` value to a known origin; null otherwise. */
export function parseMessageEditorOrigin(
  value: string | null,
): MessageEditorOrigin | null {
  return MESSAGE_EDITOR_ORIGIN_VALUES.includes(value as MessageEditorOrigin)
    ? (value as MessageEditorOrigin)
    : null;
}

/**
 * Get the edit/view URL for a message.
 *
 * Accepts a minimal structural shape so campaign messages, concept messages,
 * and message-plan candidates can all share it. Both editors
 * (ContentLibraryMessageWorkspace, ContentCopyEditor) require a `sessionId`
 * query param — without it they redirect to a list page.
 *
 * When `basePath` is provided, generates nested URLs like:
 *   `{basePath}/messages/{emailId}/edit?sessionId=...`
 * Otherwise falls back to the top-level route:
 *   `/lifecycle-studio/messages/{emailId}/edit?sessionId=...`
 *
 * `from` stamps the opening surface on edit URLs (see MessageEditorOrigins);
 * session-less view URLs skip it since the editor redirects away from them.
 */
export function getMessageEditUrl(
  message: {
    channel?: string | null;
    content_email?: { id: string; llm_chat_session_id?: string | null } | null;
    content_artifact?: {
      id: string;
      created_by_llm_chat_session_id?: string | null;
    } | null;
  },
  options?: { basePath?: string; from?: MessageEditorOrigin },
): string | null {
  const base = options?.basePath ?? "/lifecycle-studio";
  const fromParam = options?.from ? `&from=${options.from}` : "";

  if (message.channel === LcmStudioChannels.Email) {
    const email = message.content_email;
    if (!email) return null;
    return email.llm_chat_session_id
      ? `${base}/messages/${email.id}/edit?sessionId=${email.llm_chat_session_id}${fromParam}`
      : `${base}/messages/${email.id}`;
  }
  const artifact = message.content_artifact;
  if (!artifact) return null;
  const sessionId = artifact.created_by_llm_chat_session_id;
  return sessionId
    ? `${base}/messages/copy/${artifact.id}/edit?sessionId=${sessionId}${fromParam}`
    : `${base}/messages/copy/${artifact.id}`;
}

/**
 * Resolve the candidate editor's back target from the `?from=` origin param.
 * Always returns a concrete route — the editor never rides browser history,
 * which broke once in-editor detours (image editor, index→edit redirect)
 * pushed extra entries onto the stack. No param (direct link, refresh) falls
 * back to the candidates list, as does a `plan` origin without a resolved plan
 * id (plan deleted, campaign query still loading).
 */
export function getMessageEditorBackPath({
  from,
  campaignId,
  planId,
}: {
  from: MessageEditorOrigin | null;
  campaignId: string | undefined;
  planId?: string | null;
}): string {
  const candidatesUrl = `/lifecycle-studio/projects/${campaignId}/candidates`;
  switch (from) {
    case MessageEditorOrigins.Orchestration:
      return `/lifecycle-studio/projects/${campaignId}/orchestration`;
    case MessageEditorOrigins.Messages:
      return "/lifecycle-studio/messages";
    case MessageEditorOrigins.Plan:
      return planId ? `${candidatesUrl}/plans/${planId}` : candidatesUrl;
    default:
      return candidatesUrl;
  }
}
